import React, { useState } from 'react'
import { useEventHistoryQuery } from '../NewCompanyPage/EventHistory/useEventHistoryQuery'
import { EventHistoryList } from '../NewCompanyPage/EventHistory/EventHistoryList'
import { EmailsModal } from '../NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { buildEmailFromEvent } from '../NewCompanyPage/EventHistory/utils'
import { useEventHistoryFilterStore } from '../NewCompanyPage/EventHistory/EventHistoryFilterStore'
import { StageColorProvider } from '../SalesStage/StageColorContext'
import { useMountEffect } from '../../hooks/useMountEffect'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import type { EmailMessage } from '../../models/emailIntegration'

interface RecommendationActivityTabProps {
  action: EnrichedAction
}

/**
 * Skeleton matching ``EventDateGroup``'s layout (pistachio date column +
 * event card stack on the right). Replaces the default spinner overlay
 * ``EventHistoryList`` shows on initial load so the panel doesn't flash
 * a centered loader before the timeline appears.
 */
const ActivityTimelineSkeleton: React.FC = () => (
  <div className="mt-4 space-y-6 animate-pulse">
    {[0, 1, 2].map((groupIdx) => (
      <div
        key={groupIdx}
        className="flex bg-pistachio-200 border border-white rounded-lg"
      >
        <div className="flex-shrink-0 w-[130px] flex">
          <div className="w-2 bg-pistachio-100 rounded-l-lg" />
          <div className="flex-1 bg-pistachio-50 p-2 space-y-1">
            <div className="h-3 w-12 rounded bg-surface-disabled" />
            <div className="h-3 w-16 rounded bg-surface-disabled" />
          </div>
        </div>
        <div className="flex-1 p-2 space-y-2">
          {[0, 1].map((cardIdx) => (
            <div
              key={cardIdx}
              className="rounded border border-line bg-surface p-2 space-y-1.5"
            >
              <div className="h-3 w-2/3 rounded bg-surface-disabled" />
              <div className="h-3 w-5/6 rounded bg-surface-disabled" />
              <div className="h-3 w-1/2 rounded bg-surface-disabled" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

/**
 * Activity timeline for the recommendation right-panel. Reuses the same
 * ``EventHistoryList`` + ``useEventHistoryQuery`` stack the deal/company
 * pages render. Rendered only when the user selects the Activity tab so
 * the request fires lazily.
 *
 * Always company-scoped (``chainProxyId`` = ``contact_company_id``). The
 * deal-scoped activity endpoint only joins emails whose
 * ``EmailThread.deal_id`` matches — most outreach is threaded to the
 * company, not a specific deal, so deal-scoping silently drops the bulk
 * of email events. Recs care about the full company picture (matching
 * what the synthesis pipeline saw at gen time) so we mirror that here.
 *
 * The shared filter store is reset on mount because it's a global
 * zustand store — without this, filters from a previously visited deal
 * or company page would leak into the panel feed.
 */
export const RecommendationActivityTab: React.FC<
  RecommendationActivityTabProps
> = ({ action }) => {
  useMountEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  })

  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    // Coerce null → undefined so the hook can skip the fetch cleanly
    // for contact_suggestion ``create`` recs (no company assigned yet).
    chainProxyId: action.contact_company_id ?? undefined,
    selectedTab: 'all',
  })

  // First-load skeleton: ``EventHistoryList``'s built-in loading state
  // is a centered spinner overlay, which feels jumpy in the sider.
  // Replace it with placeholder rows on the initial fetch (no data yet).
  // Once events have arrived, defer back to the list so background
  // refetches don't blow away the existing timeline.
  const showInitialSkeleton = isLoading && groupedEvents.length === 0

  return (
    <StageColorProvider value="pipeline">
      {showInitialSkeleton ? (
        <ActivityTimelineSkeleton />
      ) : (
        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={() => {}}
          onEmailClick={(event) => setSelectedEmail(buildEmailFromEvent(event))}
        />
      )}
      {selectedEmail && (
        <EmailsModal
          show={!!selectedEmail}
          onHide={() => setSelectedEmail(null)}
          emailOnlyMode
          initialEmail={selectedEmail}
        />
      )}
    </StageColorProvider>
  )
}
